import React, { useState, useMemo } from 'react';
import Localize from 'react-intl-universal';

//MUI
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Container from '@mui/material/Container';
import Pagination from '@mui/material/Pagination';
import Stack from '@mui/material/Stack';
import { styled } from '@mui/material/styles';
import Typography from '@mui/material/Typography';

import NewsCard from '@components/NewsCard/NewsCard';
import useNews from '@pages/AllNews/hooks/useNews';

const StyledBox = styled(Box)(({ theme }) => ({
  padding: '2rem',
  background: theme.palette.warning.light,
  borderRadius: '5px'
}));

const StyledContainer = styled(Container)(({ theme }) => ({
  minHeight: '100vh',
  marginTop: '-2.92rem',
  paddingTop: '2.92rem',
  paddingBottom: '2.5rem',
  marginBottom: '-2.5rem',
  background: theme.palette.background.primary
}));

const AllNews = ({ configuration }) => {
  const [btnDisabled, setBtnDisabled] = useState(false);
  const isInfinityList = configuration?.global?.newsInfinityScroll;
  const config = configuration?.Default;

  const memoizedImageSource = useMemo(() => {
    if (!config?.imgPlaceholder) return null;

    try {
      return new URL(`../../assets/Images/${config?.imgPlaceholder}`, import.meta.url).href;
    } catch (e) {
      console.warn('Image not found:', config?.imgPlaceholder);
      return null;
    }
  }, [config?.imgPlaceholder]);

  const { newsData, totalPages, currentPage, setCurrentPage, setSearchParams, isLoading } =
    useNews(configuration);

  const handlePageChange = (evt, page) => {
    setCurrentPage(page);
    if (typeof setSearchParams === 'function') {
      setSearchParams((prev) => ({ ...prev, page: page - 1 }));
    }
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };
  const loadMore = () => {
    if (totalPages > currentPage) {
      setCurrentPage((prevPageNumber) => prevPageNumber + 1);
      setSearchParams((prev) => ({ ...prev, page: currentPage }));
      if (totalPages === currentPage + 1) {
        setBtnDisabled(true);
      }
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  return (
    <>
      <StyledContainer component='main'>
        {/* Loader */}
        {isLoading ? (
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              height: '60vh'
            }}
          >
            <CircularProgress size={60} />
          </Box>
        ) : (
          <Box sx={{ marginTop: 2 }}>
            <Box sx={{ minHeight: '85vh' }}>
              {newsData?.length ? (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'start' }}>
                  {newsData.map((news, index) => (
                    <NewsCard
                      key={index}
                      news={news}
                      style={{
                        width: '23.755%',
                        mediumWidth: '24.25%',
                        mb: '2rem',
                        mr: '1rem',
                        height: '8rem'
                      }}
                      memoizedImageSource={memoizedImageSource}
                    />
                  ))}
                </Box>
              ) : (
                <StyledBox>
                  <Typography>{Localize.get('Catalog.noEvents')}</Typography>
                </StyledBox>
              )}
            </Box>

            {/* Pagination */}
            <Stack
              alignItems='center'
              sx={{
                marginTop: 1,
                display: 'flex',
                justifyContent: 'center',
                position: !isInfinityList ? 'relative' : 'inherit',
                minHeight: '2.5rem'
              }}
            >
              {!isInfinityList ? (
                <Box sx={{ position: 'absolute', bottom: { xs: '-0.5em', sm: '-0.6em' } }}>
                  {totalPages > 1 && (
                    <Pagination
                      count={totalPages}
                      page={currentPage}
                      onChange={(event, page) => handlePageChange(event, page)}
                      sx={{ marginBottom: 1.2 }}
                    />
                  )}
                </Box>
              ) : (
                <Box sx={{ position: 'absolute' }}>
                  <Button disabled={btnDisabled} onClick={loadMore}>
                    {Localize.get('Catalog.loadMore')}
                  </Button>
                </Box>
              )}
            </Stack>
          </Box>
        )}
      </StyledContainer>
    </>
  );
};

export default AllNews;
